import {TimeRangeQuery} from "./TimeRangeQuery.js";
import {Group} from "./Group.js";
import {Filters} from "./Filter.js";
import {Timeseries} from "./Timeseries.js";
import {isEmpty, stringToColor} from "weed-js";
import {Field} from "./Field.js";
import {Fetcher} from "./Fetcher.js";


export class Report {

    constructor(service, uri, title, fields, relatedModels, calculation,
                description, rollups, parent
    ) {
        this.title = title;
        this.calculation = calculation;
        this.description = description;
        this.parent = parent;
        this.clones = [];

        this._fields = fields;
        this._relatedModels =
            isEmpty(parent) ? relatedModels : parent.getRelatedModels();
        this._data = isEmpty(parent) ? [] : parent.getData();
        this._labels = isEmpty(parent) ? [] : parent.getLabels();
        this._timestampField = isEmpty(parent) ? fields.find(f => f.timestamp) :
                               parent.getTimestampField();
        this._timeRangeQuery =
            isEmpty(parent) ? null : parent.getTimeRangeQuery();
        this._observationField =
            isEmpty(parent) ? fields.find(f => f.observations) :
            parent.getObservationField();
        this._rollups = isEmpty(parent) ? rollups || [] : parent.getRollups();
        this._defaultGrouping =
            isEmpty(parent) ? [] : parent.getDefaultGrouping();
        this._fetcher = isEmpty(parent) ? new Fetcher(this, service, uri) :
                        parent.getFetcher();

        if(isEmpty(parent)) {
            if(!this._observationField) {
                throw new Error("You must indicate which field on your observed model represents the observations being plotted by setting the option {observations: true}")
            }

            if(isEmpty(this._timestampField)) {
                this._timestampField = new Field("timestamp", {
                    displayName: "Date",
                    timestamp:   true,
                    groupable:   false
                });
                this._fields.push(this._timestampField);
            }

            if(!this._timestampField) {
                throw new Error("You must label one of your fields with timestamp=true. That is the name of the timestamp field on the Observation model.")
            }

            this._groups = new Group(this.fields());
            this._filters = new Filters(this.fields())
            this._timeseries = new Timeseries([]);
            this._timeseries.filter(this);

            this.validate();
        } else {
            parent.clones.push(this);
            this._groups = parent.getGroups();
            this._filters = parent.getFilters();
            this._timeseries = parent.getTimeSeries();
            this._timestampField = parent.getTimestampField();
        }

    }

    clear() {
        if(!isEmpty(this.parent)) {
            this.parent.clear();
        }
        this._timeseries = new Timeseries([]);
        this._timeseries.filter(this);
        this._groups = new Group(this.fields());
        this._filters = new Filters(this.fields())
    }

    getObservationField() {
        if(!isEmpty(this.parent)) {
            return this.parent.getObservationField();
        }
        return this._observationField;
    }

    getRollups() {
        if(!isEmpty(this.parent)) {
            return this.parent.getRollups();
        }
        return this._rollups;
    }

    getLastFetch() {
        if(!isEmpty(this.parent)) {
            return this.parent.getLastFetch();
        }
        return this._fetcher.getLastFetch();
    }

    getData() {
        if(!isEmpty(this.parent)) {
            return this.parent.getData();
        }
        return this._data;
    }

    getLabels() {
        if(!isEmpty(this.parent)) {
            return this.parent.getLabels();
        }
        return this._labels;
    }

    getFetcher() {
        if(!isEmpty(this.parent)) {
            return this.parent.getFetcher();
        }
        return this._fetcher;
    }

    getTimeRangeQuery() {
        if(!isEmpty(this.parent)) {
            return this.parent.getTimeRangeQuery();
        }
        return this._timeRangeQuery;
    }

    getTimestampField() {
        if(!isEmpty(this.parent)) {
            return this.parent.getTimestampField();
        }
        return this._timestampField;
    }

    isEmpty() {
        if(!isEmpty(this.parent)) {
            return this.parent.isEmpty();
        }
        return isEmpty(this.getTimeSeries().dataPoints);
    }

    isLoading() {
        if(!isEmpty(this.parent)) {
            return this.parent.isLoading();
        }
        return this._fetcher.isLoading();
    }

    isErrored() {
        if(!isEmpty(this.parent)) {
            return this.parent.isErrored();
        }
        return this._fetcher.isErrored();
    }

    getError() {
        if(!isEmpty(this.parent)) {
            return this.parent.getError();
        }
        return this._fetcher.getLastError();
    }

    isAggregate(type) {
        if(type === "pie") {
            return true;
        } else if(type === "doughnut") {
            return true;
        }
        return false;
    }

    getQueryLastUpdatedAt() {
        if(!isEmpty(this.parent)) {
            return this.parent.getQueryLastUpdatedAt();
        }
        return this.timeRangeQuery && this.timeRangeQuery.getLastUpdated()
    }

    validate() {
        if(isEmpty(this.title)) {
            throw new Error("Each report must have a title");
        }
        if(isEmpty(this.calculation)) {
            throw new Error(`${this.title}: Each report must have a calculation`);
        }
        const uniqueDisplayNames = new Set();
        const fields = this.fields();
        // We get redundant fields when the same field is labeled as the
        // observation field and the timestamp field.
        let redundant = 0;
        let seen = new Set();
        for(let i = 0; i < fields.length; i++) {
            if(seen.has(fields[i])) {
                redundant++;
            }
            seen.add(fields[i]);
            uniqueDisplayNames.add(fields[i].displayName);
        }
        if(uniqueDisplayNames.size !== (fields.length - redundant)) {
            console.error("Unique names", uniqueDisplayNames, "field names", fields.map(f => f.displayName));
            throw new Error(`${this.title}: Each field must have a unique display name in a given report`)
        }
    }

    getRelatedModels() {
        if(!isEmpty(this.parent)) {
            return this.parent.getRelatedModels();
        }
        return this._relatedModels;
    }

    fields() {
        if(isEmpty(this.parent)) {
            const relatedFields = this.getRelatedModels().reduce((agg,
                                                                  relatedModel
                                                                 ) => {
                return [...agg, ...relatedModel.fields]
            }, []);
            return [...this._fields, ...relatedFields];
        }
        return this.parent.fields();
    }

    getChartOptions() {
        return {
            responsive: true,
            plugins:    {
                legend:   {
                    position: "top"
                }, title: {
                    display: false,
                    text:    this.title
                },
            },
        };
    }

    getTimeSeries() {
        if(!isEmpty(this.parent)) {
            return this.parent.getTimeSeries();
        }
        return this._timeseries;
    }

    getFilters() {
        if(!isEmpty(this.parent)) {
            return this.parent.getFilters();
        }
        return this._filters;
    }

    getAggregateDatasets(type) {
        if(!isEmpty(this.parent)) {
            return this.parent.getAggregateDatasets(type);
        }
        const data = this.getTimeSeries().filter(this).aggregate(type, this);
        const labels = this.getTimeSeries().getXValues(type, this);
        return [
            {
                borderColor:     labels.map(label => {
                    const [r, g, b] = stringToColor(label);
                    const [bgr, bgg, bgb] = [
                        Math.max(r - 20, 0),
                        Math.max(g - 20, 0),
                        Math.max(b - 20, 0)
                    ];
                    return `rgb(${bgr}, ${bgg}, ${bgb})`;
                }),
                backgroundColor: labels.map(label => {
                    const [r, g, b] = stringToColor(label);
                    return `rgb(${r}, ${g}, ${b})`;
                }),
                borderWidth:     labels.map(label => 2),
                data:            data.map(d => d.observe())
            }
        ];

    }

    getChartDatasets(type) {
        /*
        Filters comes from a list of auto-generated pill boxes of values that can be either included or excluded from
        display. If a particular field value is not excluded, it's included.

        Groups are sets of fields by which to group series data. A group can be, e.g. "category", or "client". At some point
        in the future

        @param string type: The type of ChartJS chart, e.g. line, bar, etc.
        @param Array[Filter] filters: An array of filters. If filtering by e.g. category, you can have a filter with
            the field Field("category") and selectedValues like "Buds". In this case, any time a category of "Buds" is
            encountered, that datapoint will be excluded from all calculations.
        @param Array[hash] timeseries: A list of datapoints over which to run the report.
         */
        if(this.isAggregate(type)) {
            return this.getAggregateDatasets(type);
        }
        if(!isEmpty(this.parent)) {
            return this.parent.getChartDatasets(type);
        }

        const filtered = this.getTimeSeries().filter(this);
        const series = filtered.group(type, this);


        return Object.entries(series).map(([name, data]) => {
            const [r, g, b] = stringToColor(name);
            const [bgr, bgg, bgb] = [
                Math.max(r - 20, 0),
                Math.max(g - 20, 0),
                Math.max(b - 20, 0)
            ]
            return {
                label:           name,
                borderColor:     `rgb(${bgr}, ${bgg}, ${bgb})`,
                backgroundColor: `rgb(${r}, ${g}, ${b})`,
                borderWidth:     2,
                data:            data instanceof Array ?
                                 data.map(d => d.observe()) : [data.observe()]
            };
        });
    }

    query(startDate, endDate, rollup, extraParams) {
        // TODO: We don't want to call this multiple times.
        if(!isEmpty(this.parent)) {
            this.parent.query(startDate, endDate, rollup, extraParams);
            return;
        }
        this.timeRangeQuery = new TimeRangeQuery(
            this.getObservationField(),
            this.getTimestampField(),
            startDate,
            endDate,
            rollup,
            extraParams
        );
    }

    getQuery() {
        if(!isEmpty(this.parent)) {
            return this.parent.getQuery();
        }
        return this.timeRangeQuery;
    }

    setDefaultGrouping() {
        if(!isEmpty(this.parent)) {
            this.parent.setDefaultGrouping();
            return this;
        }
        const byCardinality = this.getGroups().fields.filter(f => f.groupable).sort((a,
                                                                                     b
                                                                                    ) => {
            return a.cardinality - b.cardinality;
        })
        const candidates = byCardinality.filter(f => f.cardinality > 1);
        const getDefaultGroup = () => {
            if(isEmpty(byCardinality)) {
                return [];
            }
            if(isEmpty(candidates)) {
                return [byCardinality[0]];
            }
            return [candidates[0]];
        }
        this._defaultGrouping = getDefaultGroup();
        return this;
    }

    getDefaultGrouping() {
        if(!isEmpty(this.parent)) {
            return this.parent.getDefaultGrouping();
        }
        return this._defaultGrouping;
    }

    fetch(onProgress) {
        // TODO: This method will be responsible for deciding just what to
        // fetch given what has been fetched already. It will also be
        // responsible for deciding whether to fetch at all.
        return this._fetcher.fetch(onProgress);
    }

    getChartData(type) {
        const labels = this.getTimeSeries().getXValues(
            type, this);  // These are independent variables!
        const datasets = this.getChartDatasets(type, this);
        return {
            labels:   labels,  // These are independent variables!
            datasets: datasets
        }
    }

    clone(title, description, calculation) {
        return new Report(
            this._fetcher.getService(),
            this._fetcher.getUri(),
            title,
            this.fields().filter(f => !f.timestamp),
            this.getRelatedModels(),
            calculation,
            description,
            this.getRollups(),
            this
        );
    }

    getGroups() {
        if(!isEmpty(this.parent)) {
            return this.parent.getGroups();
        }
        return this._groups;
    }

    isGroupable() {
        return this.getGroups().fields.length > 0;
    }

    shouldHide() {
        if(!isEmpty(this.parent)) {
            return this.parent.shouldHide();
        }
        return this.isErrored() || this.isLoading() ||
               (this.isAggregate() && isEmpty(this.getGroups().groupedFields));
    }

    getChartState(type, chartRef) {
        return {
            type:    type,
            options: this.getChartOptions(),
            data:    this.getChartData(type)
        }
    }
}
